// from tenant
import React, {useState, useEffect, useMemo} from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Grid,
    Paper,
    Divider,
    TextField,
    CircularProgress,
    Alert
} from '@mui/material';
import {MessageCircle, Image as ImageIcon} from 'react-feather';
import {statusColors, defaultStatus, priorityColors, defaultPriority} from '../mockData';
import AttachmentsList from './AttachmentList';
import CommentsList from './CommentsList';
import {ApiDetails, KEY} from "../../../dummyData";
import CryptoJS from "crypto-js";

// Helper functions to get status and priority styles
const getStatusColor = (status) => {
    return statusColors[status] || defaultStatus;
};

const getPriorityColor = (priority) => {
    return priorityColors[priority] || defaultPriority;
};

// Helper for formatting dates
const formatDate = (dateString) => {
    try {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        console.error("Date formatting error:", error);
        return 'Invalid date';
    }
};

// Import correct icon based on status
const getStatusIcon = (status) => {
    const statusInfo = getStatusColor(status);
    let Icon;

    switch (statusInfo.icon) {
        case 'AlertTriangle':
            Icon = props => <span className="material-icons" style={{fontSize: 14}}>warning</span>;
            break;
        case 'Clock':
            Icon = props => <span className="material-icons" style={{fontSize: 14}}>schedule</span>;
            break;
        case 'CheckCircle':
            Icon = props => <span className="material-icons" style={{fontSize: 14}}>check_circle</span>;
            break;
        case 'XCircle':
            Icon = props => <span className="material-icons" style={{fontSize: 14}}>cancel</span>;
            break;
        default:
            Icon = props => <span className="material-icons" style={{fontSize: 14}}>warning</span>;
    }

    return <Icon/>;
};

/**
 * Formats Draft.js content state into plain text
 * @param {string|Object} content - Draft.js content state as string or object
 * @returns {string} Formatted plain text
 */
const formatDraftJsContent = (content) => {
    // If content is empty or undefined, return empty string
    if (!content) return '';

    // Parse the content if it's a string
    let contentState;
    if (typeof content === 'string') {
        try {
            contentState = JSON.parse(content);
        } catch (error) {
            // If not valid JSON, return the original string
            return content;
        }
    } else {
        contentState = content;
    }

    // Check if it's a valid Draft.js content state
    if (!contentState.blocks || !Array.isArray(contentState.blocks)) {
        // If not in expected format, return original content as string
        return typeof content === 'string' ? content : JSON.stringify(content);
    }

    // Extract text from blocks with appropriate line breaks
    const textBlocks = contentState.blocks.map(block => block.text || '');

    // Join blocks with line breaks
    return textBlocks.join('\n');
};

/**
 * Checks if a string is a Draft.js content state
 * @param {string} text - Text to check
 * @returns {boolean} True if it appears to be a Draft.js content state
 */
const isDraftJsContent = (text) => {
    if (!text || typeof text !== 'string') return false;

    // Check for typical Draft.js structure markers
    return text.includes('"blocks"') && text.includes('"entityMap"');
};

const TicketDetailsDialog = ({
                                 open,
                                 onClose,
                                 ticket: initialTicket,
                                 onUpdate,
                                 showAlert,
                                 openImagePreview
                             }) => {


    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );

    let originalText = details.toString(CryptoJS.enc.Utf8);

    // State management - following landlord pattern
    const [currentTicket, setCurrentTicket] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [newCommentLoading, setNewCommentLoading] = useState(false);

    // Get tenant ID - following similar pattern to landlord getLandlordId()
    const getTenantId = () => {
        try {
            // Implement your tenant ID retrieval logic here
            // This is just a placeholder based on the landlord implementation
            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("tenantMap"),
                "YOUR_KEY_HERE" // Replace with your actual key
            );
            let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error getting tenant ID:", error);
            setError("Unable to get tenant ID. Please try again.");
            return null;
        }
    };

    // Initialize ticket when the dialog opens
    useEffect(() => {
        if (open && initialTicket) {
            setCurrentTicket({...initialTicket});

            // If ticket is first opened, fetch latest data
            if (initialTicket && initialTicket.id) {
                fetchTicketDetails(initialTicket.id);
            }
        }
    }, [open, initialTicket]);

    // Format the description if it's a Draft.js content state
    const formattedDescription = useMemo(() => {
        if (!currentTicket || !currentTicket.description) return '';

        if (isDraftJsContent(currentTicket.description)) {
            return formatDraftJsContent(currentTicket.description);
        }

        return currentTicket.description;
    }, [currentTicket]);

    // Fetch latest ticket details
    const fetchTicketDetails = async (ticketId) => {
        try {
            setLoading(true);
            /*const tenantId = getTenantId();
            if (!tenantId) return;*/

            console.log("initialTicket".toUpperCase())
            console.log(initialTicket)

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tenant/tickets/get", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    ticket_id: ticketId,
                    // landlord_id: initialTicket.landlord_id, // Use initial ticket's landlord_id
                    landlord_id: new Map(
                        Object.entries(JSON.parse(originalText))
                    ).get("landlord_id").toString(),
                })
            });

            if (response.status === 200) {
                const ticketData = await response.json();

                ticketData.createdAt = ticketData.date_created;
                ticketData.updatedAt = ticketData.date_updated;

                setCurrentTicket(ticketData);

                // Update parent's state if callback provided
                onUpdate?.(ticketData);

                setError(null);
                console.log("Ticket data updated:", ticketData);
            } else if (response.status === 404) {
                showAlert?.("Ticket not found", "error");
                onClose();
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error fetching ticket details:", error);
            showAlert?.("Failed to load ticket details", "error");
            setError("Failed to load ticket details");
        } finally {
            setLoading(false);
        }
    };

    // Handle comment changes
    const handleNewCommentChange = (e) => {
        setNewComment(e.target.value);
    };

    // Submit comment
    const submitComment = async () => {
        try {
            setNewCommentLoading(true);
            /*const tenantId = getTenantId();
            if (!tenantId) return;*/

            const commentData = {
                issue_id: currentTicket.id,
                comment: newComment,
                author_id: currentTicket.tenant_id,
                author_type: "Tenant",
                is_internal: false // Tenants can't make internal comments
            };

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tenant/tickets/comment/add", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify(commentData)
            });

            if (response.status === 200) {
                // Clear comment
                setNewComment('');
                showAlert?.("Comment added successfully", "success");

                // Refresh ticket details to get updated comments
                fetchTicketDetails(currentTicket.id);
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error adding comment:", error);
            showAlert?.("Failed to add comment", "error");
        } finally {
            setNewCommentLoading(false);
        }
    };

    // Return null early if there's no ticket
    if (!currentTicket) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{py: 2, px: 3, backgroundColor: '#f8fafc'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                        Ticket {currentTicket.id}
                    </Typography>
                    <Chip
                        icon={getStatusIcon(currentTicket.status)}
                        label={currentTicket.status}
                        size="small"
                        sx={{
                            backgroundColor: getStatusColor(currentTicket.status).bg,
                            color: getStatusColor(currentTicket.status).color,
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 500
                        }}
                    />
                </Box>
            </DialogTitle>

            <DialogContent sx={{p: 0}}>
                {error && <Alert severity="error" sx={{m: 2}}>{error}</Alert>}

                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <Box sx={{p: 3}}>
                        <Typography variant="h6" sx={{fontWeight: 600, mb: 2}}>
                            {currentTicket.title}
                        </Typography>

                        <Grid container spacing={2} sx={{mb: 3}}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2" color="textSecondary">Category</Typography>
                                <Typography variant="body1">{currentTicket.category}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2" color="textSecondary">Priority</Typography>
                                <Chip
                                    label={currentTicket.priority}
                                    size="small"
                                    sx={{
                                        backgroundColor: getPriorityColor(currentTicket.priority).bg,
                                        color: getPriorityColor(currentTicket.priority).color,
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        mt: 0.5
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2" color="textSecondary">Created</Typography>
                                <Typography
                                    variant="body1">{formatDate(currentTicket.createdAt)}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2" color="textSecondary">Last Updated</Typography>
                                <Typography
                                    variant="body1">{formatDate(currentTicket.updatedAt)}</Typography>
                            </Grid>
                        </Grid>

                        <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600}}>Description</Typography>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                backgroundColor: '#F9FAFB',
                                borderRadius: '8px'
                            }}
                        >
                            {formattedDescription.split('\n').map((paragraph, index) => (
                                <Typography key={index} variant="body2"
                                            paragraph={index < formattedDescription.split('\n').length - 1}>
                                    {paragraph || '\u00A0'}
                                </Typography>
                            ))}
                        </Paper>

                        {/* Display attachments if any */}
                        {currentTicket.attachments && currentTicket.attachments.length > 0 && (
                            <AttachmentsList attachments={currentTicket.attachments}
                                             openImagePreview={openImagePreview}/>
                        )}

                        <Divider sx={{my: 3}}/>

                        {/* Comments section */}
                        <Box sx={{mb: 2}}>
                            <Typography variant="subtitle2" sx={{
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <MessageCircle size={16}/>
                                Comments ({currentTicket.comments ? currentTicket.comments.length : 0})
                            </Typography>
                        </Box>

                        <CommentsList comments={currentTicket.comments || []}/>

                        {/* Only show comment box for open/in progress tickets */}
                        {(currentTicket.status === 'Open' || currentTicket.status === 'In Progress') && (
                            <Box sx={{mt: 2}}>
                                <Typography variant="subtitle2" sx={{mb: 1}}>Add Comment</Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="Type your comment here..."
                                    value={newComment}
                                    onChange={handleNewCommentChange}
                                    variant="outlined"
                                    disabled={newCommentLoading}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px'
                                        }
                                    }}
                                />
                                <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={!newComment.trim() || newCommentLoading}
                                        onClick={submitComment}
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        {newCommentLoading ? (
                                            <CircularProgress size={24} color="inherit"/>
                                        ) : (
                                            'Add Comment'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{py: 2, px: 3, borderTop: '1px solid #e2e8f0'}}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none'
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TicketDetailsDialog;